/**
 * useSlips Hook
 * Custom hook for managing slip list data fetching and state
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchSlips } from '@/src/lib/api/slip-api'
import { SlipTransaction, SlipFilters } from '@/src/types/slip'

interface UseSlipsResult {
  slips: SlipTransaction[]
  loading: boolean
  error: string | null
  pagination: {
    current_page: number
    per_page: number
    total_items: number
    total_pages: number
  }
  refetch: () => Promise<void>
  setFilters: (filters: SlipFilters) => void
}

export function useSlips(initialFilters?: SlipFilters): UseSlipsResult {
  const [slips, setSlips] = useState<SlipTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<SlipFilters>(initialFilters || {})
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 0,
  })

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetchSlips(filters)
      
      if (response.success) {
        setSlips(response.data.slips)
        setPagination(response.data.pagination)
      } else {
        setError('Failed to fetch slips')
      }
    } catch (err) {
      // Fallback to empty data when API not available
      console.warn('API not available, using empty data:', err)
      setSlips([])
      setPagination({
        current_page: 1,
        per_page: 10,
        total_items: 0,
        total_pages: 0,
      })
      setError(null) // Don't show error, just use empty state
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(async () => {
    await fetchData()
  }, [fetchData])

  return {
    slips,
    loading,
    error,
    pagination,
    refetch,
    setFilters,
  }
}
